<template>
  <div>
    <section v-if="loadStatus === 'pending'" class="text-center py-5" role="status" aria-live="polite">
      <div class="spinner-border mb-3" aria-hidden="true"></div>
      <h2 class="h4">{{ $t("requestErrors.loading") }}</h2>
      <p>{{ $t("requestErrors.loadingHint") }}</p>
    </section>
    <RequestError v-if="loadError" :error="loadError" retry @retry="loadForm" />
    <PostReview
      :errors="errors"
      :status="status"
      :submitting="submitting"
      @backToForm="resumeForm"
      @backToOverview="$router.push({ name: 'RoomList' })"
    />
    <div v-if="loadStatus === 'success'" v-show="!status.review" class="row">
      <div
        class="col-md-8"
      >
        <ValidationObserver
          v-slot="vo"
          ref="ratingForm"
        >
          <form @submit.prevent="checkThenSubmit(vo)">
            <div class="row">
              <div class="col-sm-10 offset-md-1">
                <fieldset
                  class="mb-3"
                  v-if="seminarDateHash"
                >
                  <legend>
                    <div class="question-container-style">
                      <span class="form-step-circle">1</span>
                      <span
                        class="question-label-style"
                        v-html="$t('ratingForm.booking.label')"
                      ></span>
                    </div>
                  </legend>
                  <ValidationProvider
                    v-slot="v"
                    rules="selected"
                    name="Name"
                  >
                    <select
                      class="form-select"
                      v-model="review.selectedBooking"
                      @change="participantChanged"
                      :class="{
                        'is-invalid': v.failed,
                        'is-valid': v.passed,
                      }"
                    >
                      <option value="0">
                        {{ $t("ratingForm.booking.choose") }}
                      </option>
                      <option
                        :value="booking"
                        v-for="booking in seminarDate.bookingIds"
                        :key="booking.id"
                      >
                        {{ booking.firstName }} {{ booking.lastName }}.
                      </option>
                    </select>
                    <small
                      class="mt-1 text-danger"
                      v-if="v.failed"
                    >{{
                      v.errors[0]
                      }}</small>
                  </ValidationProvider>
                </fieldset>
                <CollapseTransition>
                  <fieldset
                    class="email-input mb-3"
                    v-if="
                      review.selectedBooking &&
                      review.selectedBooking.hasEmail === false
                    "
                  >
                    <legend>
                      <span class="form-step-circle"><font-awesome-icon icon="fa-info" /></span>
                      {{ $t("ratingForm.email.label") }}:
                    </legend>
                    <ValidationProvider
                      v-slot="v"
                      rules="required|email"
                      name="E-Mail"
                    >
                      <input
                        type="email"
                        v-model="review.email"
                        class="form-control"
                        :class="{
                          'is-invalid': v.touched && v.failed,
                          'is-valid': v.passed,
                        }"
                      />
                      <small
                        class="mt-1 text-danger"
                        v-if="v.failed"
                      >{{
                        v.errors[0]
                        }}</small>
                    </ValidationProvider>
                  </fieldset>
                </CollapseTransition>

                <div id="questions">
                  <!-- Rating-Trainer -->
                  <fieldset class="rating mb-3">
                    <legend>
                      <span class="form-step-circle">{{
                        bookingHash ? 1 : 2
                        }}</span><span
                        class="question-label-style"
                        v-html="$t('ratingForm.trainer.rating.label')"
                      ></span>
                    </legend>
                    <div class="row">
                      <div class="col-sm-6">
                        <div class="form-group">
                          <label class="form-group-label-style">a)
                            {{ $t("ratingForm.trainer.rating.subject") }}</label>
                          <div class="mt-2">
                            <ValidationProvider
                              v-slot="v"
                              rules="required"
                              name="trainerRating1"
                            >
                              <DefaultRating
                                v-model="review.trainerSubject"
                                :validation="v.errors"
                              />
                            </ValidationProvider>
                          </div>
                        </div>
                      </div>

                      <div class="col-sm-6">
                        <div class="form-group">
                          <label class="form-group-label-style">b)
                            {{ $t("ratingForm.trainer.rating.method") }}</label>
                          <div class="mt-2">
                            <ValidationProvider
                              v-slot="v"
                              rules="required"
                              name="trainerRating2"
                            >
                              <DefaultRating
                                v-model="review.trainerMethod"
                                :validation="v.errors"
                              />
                            </ValidationProvider>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Comment -->
                    <CollapseTransition>
                      <div
                        class="form-group comment mt-3"
                        v-if="review.trainerSubject && review.trainerMethod"
                      >
                        <div class="row">
                          <label class="col-sm-12">{{
                            $t("ratingForm.trainer.comment.label")
                            }}</label>
                          <div class="col-sm-8">
                            <ProgressTextArea
                              v-model="review.trainerComment"
                              :vp="{ name: 'Trainer-Kommentar' }"
                              :placeholder="$t('ratingForm.trainer.comment.placeholder')
                                "
                            />
                          </div>
                          <div class="col-sm-4">
                            <div class="well-tip">
                              <p>
                                <strong>{{
                                  $t(
                                    "ratingForm.trainer.comment.guidance.label"
                                  )
                                }}</strong>
                              </p>
                              <ul>
                                <li>
                                  {{
                                    $t("ratingForm.trainer.comment.guidance.h1")
                                  }}
                                </li>
                                <li>
                                  {{
                                    $t("ratingForm.trainer.comment.guidance.h2")
                                  }}
                                </li>
                                <li>
                                  {{
                                    $t("ratingForm.trainer.comment.guidance.h3")
                                  }}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapseTransition>
                  </fieldset>

                  <!-- Rating-Seminar -->
                  <fieldset class="rating mb-3">
                    <legend>
                      <span class="form-step-circle">{{
                        bookingHash ? 2 : 3
                        }}</span><span
                        class="question-label-style"
                        v-html="$t('ratingForm.seminar.rating.label')"
                      ></span>
                    </legend>
                    <div class="form-group">
                      <div
                        id="stars-seminar"
                        class=""
                      >
                        <ValidationProvider
                          v-slot="v"
                          rules="required"
                          name="seminarRating"
                        >
                          <DefaultRating
                            v-model="review.seminarRating"
                            :validation="v.errors"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                    <!-- Comment -->
                    <CollapseTransition>
                      <div
                        class="form-group comment mt-3"
                        v-if="review.seminarRating"
                      >
                        <div class="row">
                          <label class="col-sm-12">{{
                            $t("ratingForm.seminar.comment.label")
                            }}</label>
                          <div class="col-sm-8">
                            <ProgressTextArea
                              v-model="review.seminarComment"
                              :vp="{ name: 'Seminar-Kommentar' }"
                              :placeholder="$t('ratingForm.seminar.comment.placeholder')
                                "
                            />
                          </div>
                          <div class="col-sm-4">
                            <div class="well-tip">
                              <p>
                                <strong>{{
                                  $t(
                                    "ratingForm.seminar.comment.guidance.label"
                                  )
                                }}</strong>
                              </p>
                              <ul>
                                <li>
                                  {{
                                    $t("ratingForm.seminar.comment.guidance.h1")
                                  }}
                                </li>
                                <li>
                                  {{
                                    $t("ratingForm.seminar.comment.guidance.h2")
                                  }}
                                </li>
                                <li>
                                  {{
                                    $t("ratingForm.seminar.comment.guidance.h3")
                                  }}
                                </li>
                                <li>
                                  {{
                                    $t("ratingForm.seminar.comment.guidance.h4")
                                  }}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapseTransition>
                  </fieldset>

                  <!-- Rating-GFU -->
                  <fieldset class="rating mb-3">
                    <legend>
                      <span class="form-step-circle">{{
                        bookingHash ? 3 : 4
                        }}</span><span
                        class="question-label-style"
                        v-html="$t('ratingForm.gfu.rating.label')"
                      ></span>
                    </legend>
                    <div class="form-group">
                      <div
                        id="stars-gfu"
                        class=""
                      >
                        <ValidationProvider
                          v-slot="v"
                          rules="required"
                          name="gfuRating"
                        >
                          <DefaultRating
                            v-model="review.gfuRating"
                            :validation="v.errors"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                    <!-- Comment -->
                    <CollapseTransition>
                      <div
                        class="form-group comment mt-3"
                        v-if="review.gfuRating"
                      >
                        <div class="row">
                          <label class="col-sm-12">{{ $t("ratingForm.gfu.comment.label") }}:</label>
                          <div class="col-sm-8">
                            <ProgressTextArea
                              v-model="review.gfuComment"
                              :vp="{ name: 'Gfu-Kommentar' }"
                              :placeholder="$t('ratingForm.gfu.comment.placeholder')
                                "
                            />
                          </div>
                          <div class="col-sm-4">
                            <div class="well-tip">
                              <p>
                                <strong>{{
                                  $t("ratingForm.gfu.comment.guidance.label")
                                  }}</strong>
                              </p>
                              <ul>
                                <li>
                                  {{ $t("ratingForm.gfu.comment.guidance.h1") }}
                                </li>
                                <li>
                                  {{ $t("ratingForm.gfu.comment.guidance.h2") }}
                                </li>
                                <li>
                                  {{ $t("ratingForm.gfu.comment.guidance.h3") }}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapseTransition>
                  </fieldset>

                  <!-- Further-Interest -->
                  <fieldset class="rating mb-3">
                    <legend>
                      <span class="form-step-circle">{{
                        bookingHash ? 4 : 5
                        }}</span><span class="question-label-style">{{
                          $t("ratingForm.furtherInterest.label")
                        }}</span>
                    </legend>
                    <div class="form-group">
                      <div class="row">
                        <div class="col-sm-8">
                          <ValidationProvider
                            v-slot="v"
                            rules=""
                            name="Seminar-Kommentar"
                          >
                            <textarea
                              class="form-control"
                              :class="{ 'is-valid': v.touched && v.passed }"
                              v-model="review.furtherInterest"
                              rows="7"
                              :placeholder="$t('ratingForm.furtherInterest.placeholder')
                                "
                            ></textarea>
                          </ValidationProvider>
                        </div>
                        <div class="col-sm-4">
                          <div class="well-tip">
                            <p>
                              <strong>{{
                                $t(
                                  "ratingForm.furtherInterest.guidance.label"
                                )
                              }}:</strong>
                            </p>
                            <div class="row">
                              <div class="col-6">
                                <ul>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h1"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h2"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h3"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h4"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h5"
                                      )
                                    }}
                                  </li>
                                </ul>
                              </div>
                              <div class="col-6">
                                <ul>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h6"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h7"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h8"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h9"
                                      )
                                    }}
                                  </li>
                                  <li>
                                    {{
                                      $t(
                                        "ratingForm.furtherInterest.guidance.h10"
                                      )
                                    }}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <fieldset>
                  <legend>
                    <span class="form-step-circle">{{
                      bookingHash ? 5 : 6
                      }}</span>

                    <span class="question-label-style">
                      {{ $t("ratingForm.anonymization.label") }}:
                    </span>
                  </legend>
                  <div class="form-group mb-3">
                    <label class="form-group-label-style">{{
                      $t("ratingForm.anonymization.name.label")
                      }}</label><br />
                    <ValidationProvider
                      v-slot="v"
                      rules="required"
                      name="Name-Anzeigen"
                    >
                      <ButtonGroup
                        v-model="review.anonymityLevelName"
                        :validation="v.errors"
                        :buttons="[
                          {
                            text: $t(
                              'ratingForm.anonymization.name.optionFullName.label'
                            ),
                            value: 1,
                            label: $t(
                              'ratingForm.anonymization.name.optionFullName.sample'
                            ),
                          },
                          {
                            text: $t(
                              'ratingForm.anonymization.name.optionAbbreviated.label'
                            ),
                            value: 2,
                            label: $t(
                              'ratingForm.anonymization.name.optionAbbreviated.sample'
                            ),
                          },
                          {
                            text: $t(
                              'ratingForm.anonymization.name.optionHideName.label'
                            ),
                            value: 0,
                          },
                        ]"
                      />
                    </ValidationProvider>
                  </div>

                  <div class="form-group mb-3">
                    <label class="form-group-label-style">{{
                      $t("ratingForm.anonymization.company.label")
                      }}</label>
                    <br />
                    <ValidationProvider
                      v-slot="v"
                      rules="required"
                      name="Firma-Anzeigen"
                    >
                      <ButtonGroup
                        v-model="review.anonymityLevelCompany"
                        :validation="v.errors"
                        :buttons="[
                          {
                            text: $t(
                              'ratingForm.anonymization.company.optionYes.label'
                            ),
                            value: 1,
                            label: $t(
                              'ratingForm.anonymization.company.optionYes.sample'
                            ),
                          },
                          {
                            text: $t(
                              'ratingForm.anonymization.company.optionNo.label'
                            ),
                            value: 0,
                          },
                        ]"
                      />
                    </ValidationProvider>
                  </div>

                  <br />

                  <div class="form-sub">
                    <div class="rating">
                      <fieldset>
                        <legend>
                          {{ $t("ratingForm.suggestions.label") }} &ndash; <u>{{ $t("ratingForm.suggestions.appendum") }}</u>
                        </legend>
                        <ValidationProvider
                          v-slot="v"
                          rules=""
                          name="Vorschlag"
                        >
                          <textarea
                            class="form-control"
                            :class="{ 'is-valid': v.touched && v.passed }"
                            v-model="review.suggestions"
                            rows="1"
                          ></textarea>
                        </ValidationProvider>
                      </fieldset>
                    </div>
                  </div>
                  <RequestError v-if="newsletterCheckError" :error="newsletterCheckError" retry @retry="checkNewsletter" />
                  <RequestError v-if="errors.newsletter" :error="errors.newsletter" keep-inputs />
                  <p v-if="status.newsletter === 'success'" class="alert alert-success" role="status">
                    {{ $t("requestErrors.newsletterDone") }}
                  </p>
                  <Newsletter
                    v-if="isSubscribed === false && status.newsletter !== 'success'"
                    :key="selectedBookingId"
                    v-model="review.newsletter"
                  />
                </fieldset>
              </div>
            </div>
            <div class="row mb-3">
              <div class="rating col-sm-10 offset-sm-1">
                <button
                  tabindex="0"
                  type="submit"
                  :disabled="submitting"
                  class="btn btn-lg btn-cta"
                >
                  {{ $t("common.submit") }}
                </button>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>

      <div
        class="col-md-4 d-none d-md-block"
      >
        <SampleReviews />
      </div>
    </div>
  </div>
</template>

<script>
import DefaultRating from "../components/DefaultRating.vue";
import ButtonGroup from "../components/ButtonGroup.vue";
import SampleReviews from "../components/SampleReviews.vue";
import Newsletter from "../components/Newsletter.vue";
import PostReview from "../components/PostReview.vue";
import ProgressTextArea from "../components/ProgressTextArea.vue";
import RequestError from "../components/RequestError.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { CollapseTransition } from "@ivanv/vue-collapse-transition";
import ratingForm from "../mixins/rating-form.js";

export default {
  name: "RatingForm",
  mixins: [ratingForm],
  components: {
    DefaultRating,
    ButtonGroup,
    ValidationProvider,
    ValidationObserver,
    SampleReviews,
    Newsletter,
    PostReview,
    ProgressTextArea,
    RequestError,
    CollapseTransition,
  },
};
</script>
